/**
 * @module app.core
 */

import { autoinject, bindingMode, newInstance } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, } from 'app/shared/lib/essette-site';
import { AssessmentService } from 'app/core/services/assessment-service';
import { AssessmentContext } from 'app/core/lib/assessment-context';
import { QuestionResolver } from 'app/core/components/assessments/question-resolver';
import { IAssessmentsConfig, ITasksConfig } from 'app/core/models/essette-interfaces';
import { IScoreChangeCustomEvent, IQuestionSavedResponse, IQuestionConfig } from 'app/core/components/assessments/interfaces';
import { apiCall } from 'app/shared/lib/essette-api';
import { getReference, fromApiDecimal } from 'app/shared/models/essette-types';
import { ICollapse } from 'app/shared/attributes/ess-collapse-attributes';
import { AssessmentAttachmentWithLocalState } from 'app/core/models/local-state-extensions';
import { AssessmentAttachmentCompletedReloadEvent, UserTrackerContinueEvent } from 'app/core/lib/events';
import { FormatProvider } from 'app/shared/lib/formatting/format-provider';
import { Toaster } from 'app/shared/lib/toaster';
import { safe } from 'app/shared/lib/utilities';
import { validate } from 'app/shared/elements/ess-rule';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { fireEvents } from 'app/shared/lib/fire-events';
import { EventAggregator } from 'aurelia-event-aggregator';
import { EnableReturnFocusEvent, TitleChangeEvent } from 'app/shared/lib/events';
import { I18N } from 'aurelia-i18n';
import { ScrollSpy } from 'materialize-css';
import { guid } from 'app/shared/lib/guid';
import { microTask, task } from 'app/shared/lib/decorators';
import { openAuthSummary } from 'app/um/components/summary/auth-summary';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

export interface IAssessmentAttachmentOptions {
    showHeader: boolean;
    showToc: boolean;
    showBack: boolean;
    showActions: boolean;
    showMember: boolean;
    showMemberSummaryLink: boolean,
    showTarget: boolean;
    showTargetSummaryLink: boolean,
    forceRequiredQuestions: boolean;
    canReopen: boolean;
    stickyActions: boolean;
    isTaskDisabled: boolean;
}

const defaultOptions: IAssessmentAttachmentOptions = {
    showHeader: true,
    showToc: true,
    showBack: true,
    showActions: true,
    showMember: true,
    showMemberSummaryLink: false,
    showTarget: true,
    showTargetSummaryLink: false,
    forceRequiredQuestions: false,
    canReopen: true,
    stickyActions: true,
    isTaskDisabled: false
}

type MappedSharedHistoryAnswer = {
    answer: Core.AssessmentAnswer;
    questionId: string;
    answeredTimestamp: string;
    sequence: number;
    answerId: string;
};

@autoinject()
export class AssessmentAttachment extends ComponentBase {

    ////****************** Fields and Properties **************************************////
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public config: IAssessmentsConfig | null = null;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public taskConfig: ITasksConfig | null = null;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public options: IAssessmentAttachmentOptions = defaultOptions;

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'attachmentIdChange' })
    public attachmentId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public readonly: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public summary: boolean = false;

    // showHistory has a change handler for responding to changes issued from outside the component (i.e. summary pages)
    @bindable({ defaultBindingMode: bindingMode.toView })
    public showHistory: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public target: Readonly<{
        id: string;
        number: string;
        member: CD.Member;
    }> | null = null;

    // showHistoryChecked tracks the state of the toggle on the component itself
    protected showHistoryChecked: boolean = false;

    protected historyExists: boolean = false;

    @bindable.string({ defaultBindingMode: bindingMode.twoWay })
    protected attachmentDisplay: string | null = null;

    protected mounted: boolean = false;

    protected attachment: AssessmentAttachmentWithLocalState | null = null;

    protected assessmentVersion: Core.AssessmentVersion | null = null;
    protected session: Core.AssessmentSession | null = null;
    protected mutationObserver: MutationObserver;
    protected scrollSpies: ScrollSpy[] | null = null;
    protected menuConfigured: boolean = false;

    protected sections: Core.AssessmentSection[] | null = null;
    private questionScores: Map<string, number> = new Map();
    protected score: string = '0';
    protected possibleScore: string = '0';

    private standardQuestionChanged: boolean = false;

    protected hasReopenAssessment: boolean = false;

    protected isValid: boolean = false;

    protected openMemberSummary = openMemberSummary;
    protected openAuthSummary = openAuthSummary;
    protected openCaseSummary = openCaseSummary;

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $collapsible!: ICollapse;

    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly $element: Element,
        @newInstance() private readonly context: AssessmentContext,
        private readonly service: AssessmentService,
        private readonly resolver: QuestionResolver,
        private readonly formatProvider: FormatProvider,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
        this.mutationObserver = new MutationObserver(this.handleDOMChange.bind(this));
    }

    protected bind(): void {
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }
        if(!this.taskConfig) { throw new Error('Bindable property [task-config] was not set'); }
        if(!this.attachmentId) { throw new Error('Bindable property [attachment-id] was not set'); }
        this.context.clear();
        this.service.configure(this.config);

        this.options = { ...defaultOptions, ...this.options };

        this.checkPermissions();

        this.loadData(this.attachmentId);

        this.mutationObserver.observe(this.$element, { childList: true, subtree: true });
    }

    protected attached(): void {
        this.mounted = true;
    }

    protected detached(): void {
        this.context.clear();
        this.mounted = false;
        this.sections = null;
        if(this.mutationObserver) {
            this.mutationObserver.disconnect();
        }
    }

    protected attachmentIdChange() {
        this.context.clear()
        this.standardQuestionChanged = false;
        this.reload();
    }

    ////****************** Events ***************************************************////
    public scoreChange(event: IScoreChangeCustomEvent): void {
        this.questionScores.set(event.detail.questionId, event.detail.score);

        this.updateTotalScore();
    }

    protected async showHistoryChanged(): Promise<void> {
        this.showHistoryChecked = this.showHistory;
        await this.reload();
    }

    protected async attachmentIdChanged(newValue: string): Promise<void> {
        this.sections = null;
        await this.loadData(newValue);
    }

    ////****************** Functions ***********************************************////
    public async reload(): Promise<void> {
        // Menu needs to be reconfigured with each reload in order to keep ScrollSpy working
        this.menuConfigured = false;
        if(this.attachmentId) {
            this.loadData(this.attachmentId);
        }
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();

        if(this.config && this.config.targetType === 'CM.Case') {
            this.hasReopenAssessment = this.options.canReopen && this.accountContext.hasPermission('CM.CaseAssessmentAttachment.Reopen');
        }
        if(this.config && this.config.targetType === 'UM.Auth') {
            this.hasReopenAssessment = this.options.canReopen && this.accountContext.hasPermission('UM.AuthAssessmentAttachment.Reopen');
        }
    }

    protected handleDOMChange() {
        // Check if all required questions have been answered to activate Complete button
        this.checkValidation();
        this.configureMenu();
    }

    @task
    protected configureMenu(): void {
        // Destroy previous scrollspy instances and rebuild them only if menu is not configured and data is finished loading
        if(!this.menuConfigured && !this.state.loading) {
            const sections = document.querySelectorAll('.assessment-section-main');
            if(sections.length !== 0) {
                this.menuConfigured = true;
                if(this.scrollSpies) this.scrollSpies.forEach((spy) => spy.destroy());
                this.scrollSpies = M.ScrollSpy.init(sections, { scrollOffset: 120 });
            }
        }
    }

    protected async confirmCancelChanges(): Promise<boolean> {
        if(this.readonly) { return true; }

        const answers = new Array<Core.AssessmentAnswer>();
        this.context.questionComponents
            .forEach(q => {
                if(!!q.getAnswers) {
                    const a = q.getAnswers() || [];
                    answers.push(...a);
                }
            });

        if(answers.length !== 0) {
            const result = await confirmation.showCancelChanges('am:assessment.$');
            if(result.dialogResponse === confirmation.options.OK) {
                return true;
            }
            else {
                return false;
            }
        }
        else {
            return true;
        }
    }

    private async toggleShowHistoryChecked(): Promise<void> {
        const toggle = this.readonly ? true : await this.confirmCancelChanges();
        if(toggle) {
            this.showHistory = !this.showHistory;
        }
    }

    private async loadData(attachmentId: string): Promise<void> {
        // Wait for attachment load before continuing
        await this.loadAttachment(attachmentId, this.showHistoryChecked);
        if(!this.attachment) { throw new Error('No assessment attachment was loaded'); }

        // Set Page title to Assessment name
        if(!!this.config?.backRouteName && !!this.attachment.$display) {
            this.events.publish(new TitleChangeEvent([this.attachment.$display]));
        }
        this.router.title = this.i18n.tr('cm:routes.case.assessments.attachment.$title', { what: this.attachment?.$display || "" });
        this.router.updateTitle();

        // if not viewing from a summary and assessment is not already readonly, then reassess assessment's readonly state
        if(!this.summary && !this.readonly) {
            this.readonly = this.attachment.isCompleted || !this.attachment?.isActive;
            //external auths (e.g. Portal) get one more criterion: have I been appending to the original author's assessments?
            //I can add and edit additional assessments, but I am forbidden from messing with the author's assessments.
            if(!this.readonly && this.attachment?.createdInPortal && this.attachment?.metadata?.created?.byUser?.id !== this.env.user.id) {
                this.readonly = false;
            }
        }

        // Set context
        this.context.attachment = this.attachment;
        this.context.taskConfig = this.taskConfig;
        this.context.currentSession.attachment = getReference(this.attachment);

        if(this.attachment.lastSession && this.attachment.lastSession.id) {
            await this.loadLastSession(this.attachment.lastSession.id);
        }

        const versionId = safe(() => this.attachment!.assessment!.version!.id);
        if(!versionId) { throw new Error('Unable to determine assessment version id'); }
        await this.loadAssessmentStructure(versionId);

        this.attachmentDisplay = this.attachment.$display || "";
    }

    private applyQuestionNumbers(results: Core.AssessmentSection[]): Core.AssessmentSection[] {
        let count = 1;
        const result = results.map((section: Core.AssessmentSection) => {
            if(section.questions) {
                // JB: using any to add "_number"
                section.questions.map((question: any) => {
                    question._number = count;
                    count++;
                    return question;
                })
            }
            return section
        })
        return result;
    }

    protected createQuestionConfig(question: Core.AssessmentQuestion, labelId: string = ''): IQuestionConfig {
        return {
            question: question,
            required: question.flag?.isRequired || false,
            //assessments are only readonly if they are completed or inactive
            readonly: this.summary || this.attachment?.isCompleted || !this.attachment?.isActive,
            showHistory: this.showHistoryChecked,
            labelId: labelId
        } as IQuestionConfig;
    }

    private calculatePossibleScore(): void {
        let totalPossibleScore: number = 0.00;
        if(this.sections && this.sections.length) {
            this.sections.forEach(s => {
                if(s.questions && s.questions.length) {
                    s.questions
                        .filter(q => {
                            if(typeof q.possibleScore !== 'string') return false;
                            return (+q.possibleScore || 0) > 0;
                        })  // Only questions with possibleScore > 0
                        .forEach(q => {
                            totalPossibleScore += (fromApiDecimal(q.possibleScore) || 0.00);
                        });
                }
            });
        }

        this.possibleScore = this.formatProvider.formatDecimal(totalPossibleScore, { format: 'fixed2' }) || '';
    }

    private updateTotalScore(): void {
        let totalScore: number = 0.00;
        const locale = 'en-US';  //TODO: get this from user preferences

        this.questionScores.forEach(q => {
            totalScore += q;
        });

        this.score = this.formatProvider.formatDecimal(totalScore, { format: 'fixed2' }) || '';
    }

    protected async navigateBack(): Promise<void> {
        if(await this.confirmCancelChanges()) {
            this.events.publish(new EnableReturnFocusEvent());
            this.events.publish(new UserTrackerContinueEvent());
            this.router.navigateBack();
        }
    }

    public async save(complete: boolean = false): Promise<void> {
        if(complete && !validate(this.$element)) {
            this.toaster.standard.invalidRequired();
            return;
        }

        const answers = new Array<Core.AssessmentAnswer>();

        this.context.questionComponents
            .forEach(q => {
                if(!!q.getAnswers) {
                    const a = q.getAnswers() || [];
                    answers.push(...a);
                }
            });

        const mappedAnswers = this.mapCurrentAnswers(answers);
        const hasAnswerChanges = this.hasAnswerChanges(mappedAnswers);
        const hasOtherQuestionChanges = this.context.unsavedChanges && this.context.questionComponents.some(q => !!q.saveAnswer);

        if(!complete && !hasAnswerChanges && !hasOtherQuestionChanges) {
            if(this.standardQuestionChanged && !validate(this.$element)) {
                this.toaster.standard.invalidRequired();
                return;
            }

            this.toaster.warn().message(this.i18n.tr('ui:messages.warnings.noChangesDetected')).show();
            return;
        }

        // Add Assessment Answers to the session
        this.context.currentSession.answers = answers;
        let isSuccessful = false;
        try {
            if(await this.saveSession()) {
                // Non-standard answers (system review, suggest goal/task) implement their own saves (IQuestionComponent.saveAnswer())
                isSuccessful = await this.saveOtherQuestions();
                // Keep UI in sync after save without triggering a full attachment reload.
                this.updateCurrentAnswers(mappedAnswers);
                this.context.unsavedChanges = false;
                this.standardQuestionChanged = false;

                if(complete) {
                    if(await this.completeAttachment()) {
                        if(this.attachment) { this.attachment.isCompleted = true; }
                        this.events.publish(new AssessmentAttachmentCompletedReloadEvent());
                        // INFO [Matt] Not every attachment should trigger a back(),
                        // e.g. an assessment smart task with follow up questions.
                        // If you want to trigger back on complete, you should respond
                        // to the attachment-complete event.
                        // this.router.navigateBack();
                    }

                    if(this.mounted) {
                        this.reload();
                    }
                }
            }
        } catch(error) {
            if(this.mounted) {
                this.reload();
            }
            throw error;
        } finally {
            this.toaster.standard.saved('am:assessment.$', isSuccessful);
        }
    }

    private mapCurrentAnswers(answers: Core.AssessmentAnswer[]): Core.AssessmentAnswer[] {
        const questionsById = new Map<string, Core.AssessmentQuestion>();
        (this.sections || []).forEach(section => {
            (section.questions || []).forEach(question => {
                if(question.id) {
                    questionsById.set(question.id, question);
                }
            });
        });

        const mappedAnswers = answers
            .filter(answer => !!answer.question?.id)
            .map(answer => {
                const questionId = answer.question!.id!;
                const question = questionsById.get(questionId);

                const mappedAnswer: Core.AssessmentAnswer = {
                    question: { id: questionId },
                    text: answer.text || null,
                    value: answer.value || answer.text || null
                };

                const sharedQuestionId = answer.sharedQuestion?.id || question?.sharedQuestion?.id;
                if(sharedQuestionId) {
                    mappedAnswer.sharedQuestion = { id: sharedQuestionId };
                }

                if(answer.choice?.id) {
                    mappedAnswer.choice = { id: answer.choice.id };

                    const choice = question?.choices?.find(c => c.id === answer.choice?.id);
                    const sharedChoiceId = answer.sharedChoice?.id || choice?.sharedChoice?.id;
                    if(sharedChoiceId) {
                        mappedAnswer.sharedChoice = { id: sharedChoiceId };
                    }
                }

                return mappedAnswer;
            });

        return mappedAnswers;
    }

    private mapSharedAnswersToCurrentAnswers(sharedAnswers: CD.MemberAssessmentSharedAnswer[], sections: Core.AssessmentSection[]): Core.AssessmentAnswer[] {
        if(!sections.length) return [];

        const questionBySharedQuestionId = new Map<string, Core.AssessmentQuestion>();
        sections.forEach(section => {
            if(!section.questions) return;
            section.questions.forEach(question => {
                const sharedQuestionId = question.sharedQuestion?.id;
                if(sharedQuestionId) questionBySharedQuestionId.set(sharedQuestionId, question);
            });
        });

        return sharedAnswers
            .filter(answer => !!answer.isLatest)
            .map(answer => {
                const sharedQuestionId = answer.sharedQuestion?.id || answer.sharedQuestionVersion?.sharedQuestion?.id;
                if(!sharedQuestionId) return {} as Core.AssessmentAnswer;

                const question = questionBySharedQuestionId.get(sharedQuestionId);
                if(!question?.id) return {} as Core.AssessmentAnswer;

                const mappedAnswer: Core.AssessmentAnswer = {
                    question: { id: question.id },
                    sharedQuestion: { id: sharedQuestionId },
                    text: answer.text || null,
                    value: answer.value || answer.text || null
                };

                if(answer.sharedChoice?.id && question.choices){
                    mappedAnswer.sharedChoice = { id: answer.sharedChoice.id };

                    const choice = question.choices.find(c => c.sharedChoice?.id === answer.sharedChoice?.id);
                    if(choice?.id) mappedAnswer.choice = { id: choice.id };
                }

                const sourceChoiceId = (answer as any).choice?.id;
                if(!mappedAnswer.choice?.id && sourceChoiceId) {
                    mappedAnswer.choice = { id: sourceChoiceId };
                }

                return mappedAnswer;
            });
    }

    private mapSharedAnswersToHistory(sharedAnswers: CD.MemberAssessmentSharedAnswer[], sections: Core.AssessmentSection[] = this.sections || []): Core.AssessmentAnswer[] {
        if(!sections) return [];
        const questionBySharedQuestionId = new Map<string, Core.AssessmentQuestion>();
        sections.forEach(section => {
            if(!section.questions) return;
            section.questions.forEach(question => {
                const sharedQuestionId = question.sharedQuestion?.id;
                if(sharedQuestionId) {
                    questionBySharedQuestionId.set(sharedQuestionId, question);
                }
            });
        });

        const mappedSharedAnswers = sharedAnswers
            .map((sourceAnswer): MappedSharedHistoryAnswer => {
                const sharedQuestionId = sourceAnswer.sharedQuestion?.id || sourceAnswer.sharedQuestionVersion?.sharedQuestion?.id;
                if(!sharedQuestionId) return {} as MappedSharedHistoryAnswer;

                const question = questionBySharedQuestionId.get(sharedQuestionId);
                if(!question?.id) return {} as MappedSharedHistoryAnswer;

                const mappedAnswer: Core.AssessmentAnswer = {
                    id: sourceAnswer.id || undefined,
                    question: { id: question.id },
                    sharedQuestion: { id: sharedQuestionId },
                    text: sourceAnswer.text || null,
                    value: sourceAnswer.value || sourceAnswer.text || null,
                    metadata: sourceAnswer.metadata
                };

                if(sourceAnswer.sharedChoice?.id) {
                    mappedAnswer.sharedChoice = { id: sourceAnswer.sharedChoice.id };
                    if(question.choices) {
                        const choice = question.choices.find(c => c.sharedChoice?.id === sourceAnswer.sharedChoice?.id);
                        if(choice?.id) {
                            mappedAnswer.choice = { id: choice.id };
                        }
                    }
                }

                const sourceChoiceId = (sourceAnswer as any).choice?.id;
                if(!mappedAnswer.choice?.id && sourceChoiceId) {
                    mappedAnswer.choice = { id: sourceChoiceId };
                }

                return {
                    answer: mappedAnswer,
                    questionId: question.id,
                    answeredTimestamp: sourceAnswer.metadata?.answered?.timestamp || '',
                    sequence: sourceAnswer.sequence || 0,
                    answerId: sourceAnswer.id || ''
                };
            });

        // Group multi-select answers for the same question answered at the same time into a single
        // history record with combined text/value (e.g. "Green, Orange" instead of two rows).
        // Matches transitional behavior.
        const grouped = new Map<string, MappedSharedHistoryAnswer[]>();
        mappedSharedAnswers.forEach(entry => {
            const groupKey = `${entry.questionId}|${entry.answeredTimestamp}`;
            const entries = grouped.get(groupKey) || [];
            entries.push(entry);
            grouped.set(groupKey, entries);
        });

        const combinedHistoryAnswers: Core.AssessmentAnswer[] = [];
        grouped.forEach((entries) => {
            entries.sort((a, b) => {
                if(a.sequence !== b.sequence) {
                    return a.sequence - b.sequence;
                }
                return a.answerId.localeCompare(b.answerId);
            });

            if(entries.length === 1) {
                combinedHistoryAnswers.push(entries[0].answer);
                return;
            }

            const first = entries[0].answer;
            const combinedText = entries
                .map(e => e.answer.text)
                .filter((text): text is string => !!text)
                .join(', ');
            const combinedValue = entries
                .map(e => e.answer.value || e.answer.text)
                .filter((value): value is string => !!value)
                .join(', ');

            combinedHistoryAnswers.push({
                ...first,
                choice: undefined,
                sharedChoice: undefined,
                text: combinedText || first.text || null,
                value: combinedValue || combinedText || first.value || first.text || null
            });
        });

        return combinedHistoryAnswers;
    }

    // Merges current answers on the assessment attachment with shared answers from other sessions/modules
    private mergeCurrentAnswers(existingAnswers: Core.AssessmentAnswer[], sharedAnswers: Core.AssessmentAnswer[]): Core.AssessmentAnswer[] {
        const mergedAnswers = [...existingAnswers];
        const existingKeys = new Set(this.normalizeCurrentAnswers(existingAnswers));

        sharedAnswers.forEach(answer => {
            const answerKey = this.normalizeCurrentAnswers([answer])[0];
            if(answerKey && !existingKeys.has(answerKey)) {
                mergedAnswers.push(answer);
            }
        });

        return mergedAnswers;
    }

    // Merges answer history with shared answer history.
    private mergeHistoryAnswers(existingAnswers: Core.AssessmentAnswer[], sharedHistoryAnswers: Core.AssessmentAnswer[]): Core.AssessmentAnswer[] {
        const historyKey = (answer: Core.AssessmentAnswer): string => JSON.stringify({
            questionId: answer.question?.id || null,
            sharedQuestionId: answer.sharedQuestion?.id || null,
            choiceId: answer.choice?.id || null,
            sharedChoiceId: answer.sharedChoice?.id || null,
            value: answer.choice?.id || answer.sharedChoice?.id ? null : answer.value || answer.text || null
        });

        const questionTimelineKey = (answer: Core.AssessmentAnswer): string =>
            `${answer.question?.id || ''}|${answer.sharedQuestion?.id || ''}`;

        // when deduplicating we want the earlier shared history answer to take precedence (matching transitional logic)
        // For instance, if user has saved the same answer in two back-to-back sessions, we want to display the answer from the first session.
        const orderedSharedHistoryAnswers = [...sharedHistoryAnswers].sort((a, b) => {
            const ta = a.metadata?.answered?.timestamp ?? '';
            const tb = b.metadata?.answered?.timestamp ?? '';
            return ta < tb ? -1 : ta > tb ? 1 : 0;
        });

        const allHistoryAnswers = [...existingAnswers, ...orderedSharedHistoryAnswers]
            .sort((a, b) => {
                const ta = a.metadata?.answered?.timestamp ?? '';
                const tb = b.metadata?.answered?.timestamp ?? '';
                return ta < tb ? -1 : ta > tb ? 1 : 0;
            });

        const seenAnswerIds = new Set<string>();
        const lastStateByQuestion = new Map<string, string>();
        const collapsedHistoryAnswers: Core.AssessmentAnswer[] = [];

        allHistoryAnswers.forEach(answer => {
            if(answer.id) {
                if(seenAnswerIds.has(answer.id)) {
                    return;
                }
                seenAnswerIds.add(answer.id);
            }

            const answerKey = historyKey(answer);
            const timelineKey = questionTimelineKey(answer);
            const lastState = lastStateByQuestion.get(timelineKey);

            // Collapse only uninterrupted repeats of the same answer state for the same question.
            // If user answers "Green," then "Green, Red", then "Green" again in a multi-select question, show all 3 sessions in history.
            if(lastState === answerKey) {
                return;
            }

            collapsedHistoryAnswers.push(answer);
            lastStateByQuestion.set(timelineKey, answerKey);
        });

        return collapsedHistoryAnswers;
    }

    private hasAnswerChanges(currentAnswers: Core.AssessmentAnswer[]): boolean {
        const existingAnswers = this.normalizeCurrentAnswers(this.context.attachment.currentAnswers || []);
        const nextAnswers = this.normalizeCurrentAnswers(currentAnswers);

        return JSON.stringify(existingAnswers) !== JSON.stringify(nextAnswers);
    }

    private normalizeCurrentAnswers(answers: Core.AssessmentAnswer[]): string[] {
        return answers
            .filter(answer => !!answer.question?.id)
            .map(answer => JSON.stringify({
                questionId: answer.question?.id || null,
                sharedQuestionId: answer.sharedQuestion?.id || null,
                choiceId: answer.choice?.id || null,
                sharedChoiceId: answer.sharedChoice?.id || null,
                value: answer.choice?.id || answer.sharedChoice?.id ? null : answer.value || answer.text || null
            }))
            .sort();
    }

    private updateCurrentAnswers(currentAnswers: Core.AssessmentAnswer[]): void {
        if(!this.context.attachment) { return; }

        this.context.attachment.currentAnswers = currentAnswers;
        this.historyExists = currentAnswers.length > 0;
    }

    private async saveOtherQuestions(): Promise<boolean> {
        const promises: Promise<IQuestionSavedResponse | null>[] = [];
        this.context.questionComponents.forEach(q => {
            if(!!q.saveAnswer) {
                promises.push(q.saveAnswer(this.context.currentSession));
            }
        })
        const responses = await Promise.all(promises);

        const allSaved = responses.every(r => { return (!!r && r.isSuccessful); });
        if(!allSaved) {
            responses.forEach(r => {
                if(!r || !r.isSuccessful) {
                    const message = !r || !r.question ? "Unknown error saving an other assessment question" : `Error saving other assessment question (${r.question.id})  ${r.question.description}`;
                    this.logger.error(message);
                }
            })
        }

        return allSaved;
    }

    // Dig through nested objects in search of questions and mark them all as Required
    private forceRequireQuestions(array: any[]): void {
        array.forEach((object: any) => {
            if(object.url?.match(/^UM\/Auths\/Assessments\/Questions\/\d+\/$/)) {
                object.flag = {
                    ...object.flag,
                    isRequired: true
                };
            };
            Object.values(object).forEach(value => {
                if(Array.isArray(value)) {
                    this.forceRequireQuestions(value);
                }
            })
        });
    }

    protected handleQuestionInput(): boolean {
        this.standardQuestionChanged = true;
        this.checkValidation();
        return true;
    }

    protected handleClick(): boolean {
        if(document.activeElement?.classList.contains('ess-button')) {
            return this.handleQuestionInput();
        }
        return true;
    }

    @microTask
    protected checkValidation(): void {
        this.isValid = validate(this.$element);
    }

    ////****************** Service Calls ********************************************////
    /**
     * Loads a Core.AssessmentAttachment, including any current answers. Does not include structure of sections, questions and choices
     * @param attachmentId The id of the attachment to load
     * @param includeHistory Whether to include collection of historical answers
     */
    @apiCall
    private async loadAttachment(attachmentId: string, includeHistory: boolean = false): Promise<void> {
        const includes: string[] = ['assessment', 'metadata.created.byUser'];
        const inactiveIncludes: string[] = ['currentAnswers'];
        const fields: string[] = [
            'assessment.version.id',
            // attachedTo is used on the context by some components
            'attachedTo.id',
            'attachedTo.type.code',
            'createdInPortal',
            'currentAnswers.*',
            'description',
            'isCompleted',
            'isScored',
            'lastSession.id',
            'member.id',
            'metadata.created.byUser.description',
            'metadata.created.timestamp',
            'task.id'
        ];
        if(includeHistory) {
            inactiveIncludes.push('history.answers', 'history.answers.metadata.answered.byUser');
            fields.push('history.answers.*', 'history.answers.metadata.answered.byUser.description');
        }

        try {
            this.state.beginLoad();
            const response = await this.service.loadAttachment(attachmentId, includes, inactiveIncludes, fields);
            this.attachment = response.results[0];
            if(this.attachment.currentAnswers && this.attachment.currentAnswers.length > 0) this.historyExists = true;
            if(this.attachment?.assessment?.version?.id) {
                await this.loadResources(this.attachment.assessment.version.id)
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    private async loadMemberSharedQuestionAnswers(memberId: string, sharedQuestionIds: string[]): Promise<CD.MemberAssessmentSharedAnswer[]> {
        try {
            this.state.beginLoad();

            const includes = [
                'attachment',
                'attachment.session',
                'choice',
                'member',
                'metadata.answered.byUser',
                'question',
                'sharedChoice',
                'sharedQuestionVersion',
                'sharedQuestionVersion.sharedQuestion'
            ];

            const response = await this.service.loadSharedQuestionAnswersByMember(memberId, sharedQuestionIds, { all: true }, includes, this.showHistoryChecked);
            return response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    // Determine which questions in the assessment are shared questions and return their shared question ids
    private getSharedQuestionIds(sections: Core.AssessmentSection[]): string[] {
        if(!sections.length) return [];

        const sharedQuestionIds = new Set<string>();
        sections.forEach(section => {
            if(!section.questions) return;
            section.questions.forEach(question => {
                const sharedQuestionId = question.sharedQuestion?.id;
                if(sharedQuestionId) sharedQuestionIds.add(sharedQuestionId);
            });
        });

        return [...sharedQuestionIds];
    }

    // Shared answers now come from distinct API source; data must be reshaped to conform to what is expected based on transitional.
    private applyMemberSharedAnswers(memberSharedAnswers: CD.MemberAssessmentSharedAnswer[], sections: Core.AssessmentSection[]): void {
        if(!this.attachment || !memberSharedAnswers.length || !sections.length) return;

        if(!this.attachment.currentAnswers) this.attachment.currentAnswers = [];

        const mappedSharedAnswers = this.mapSharedAnswersToCurrentAnswers(memberSharedAnswers, sections);
        const sharedQuestionIds = new Set(this.getSharedQuestionIds(sections));
        const sharedAssessmentQuestionIds = new Set(
            sections
                .flatMap(s => s.questions || [])
                .filter(q => !!q.sharedQuestion?.id)
                .map(q => q.id)
                .filter((id): id is string => !!id)
        );

        if(mappedSharedAnswers.length) {
            this.attachment.currentAnswers = this.mergeCurrentAnswers(this.attachment.currentAnswers, mappedSharedAnswers);
        }

        // Remove shared-question answers loaded by the regular history include;
        // shared history should come only from the shared answers API mapping.
        if(this.showHistoryChecked && this.attachment.history?.answers?.length) {
            this.attachment.history.answers = this.attachment.history.answers.filter(answer => {
                const answerSharedQuestionId = answer.sharedQuestion?.id;
                const answerQuestionId = answer.question?.id;

                if(answerSharedQuestionId && sharedQuestionIds.has(answerSharedQuestionId)) return false;
                if(answerQuestionId && sharedAssessmentQuestionIds.has(answerQuestionId)) return false;

                return true;
            });
        }

        if(this.showHistoryChecked) {
            const mappedHistoryAnswers = this.mapSharedAnswersToHistory(memberSharedAnswers, sections);
            if(mappedHistoryAnswers.length > 0) {
                if(!this.attachment.history) this.attachment.history = {};
                if(!this.attachment.history.answers) this.attachment.history.answers = [];
                this.attachment.history.answers = this.mergeHistoryAnswers(this.attachment.history.answers, mappedHistoryAnswers);
            }
        }

        if(!this.historyExists) {
            this.historyExists = this.attachment.currentAnswers.length > 0;
        }
    }

    @apiCall
    private async loadResources(versionId: string): Promise<void> {
        const includes: string[] = ['resources'];
        const fields: string[] = [
            'resources.description',
            'resources.href',
            'resources.id',
            'resources.summary'
        ];
        try {
            this.state.beginLoad();
            const response = await this.service.loadVersion(versionId, includes, fields);
            this.assessmentVersion = response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    /** Loads the structure of sections, questions, and choices for a given assessment version */
    @apiCall
    private async loadAssessmentStructure(versionId: string): Promise<void> {
        // TODO: Find fix for Table of Contents, ToC focus stops working after section is reloaded.

        // Ensure question resolver is initialized before loading structure
        if(!this.resolver.isInitialized) {
            await this.resolver.init();
        }

        const fields: string[] = [
            'description',
            'questions.*'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadVersionSectionsWithStructure(versionId, fields);
            this.context.questionComponents = [];  // Clear any previous question components
            const loadedSections = this.applyQuestionNumbers(response.results);

            // Incorporate any member shared answers for shared questions
            if(this.target?.member?.id) {
                const sharedQuestionIds = this.getSharedQuestionIds(loadedSections);
                if(sharedQuestionIds.length) {
                    const memberSharedAnswers = await this.loadMemberSharedQuestionAnswers(this.target.member.id, sharedQuestionIds);
                    this.applyMemberSharedAnswers(memberSharedAnswers, loadedSections);
                }
            }

            this.sections = loadedSections;

            //  If true, all questions are required regardless of assessment configuration
            if(this.options.forceRequiredQuestions) this.forceRequireQuestions(this.sections);
            this.calculatePossibleScore();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    /** Loads Assessment Session information for a given id */
    @apiCall
    private async loadLastSession(sessionId: string): Promise<void> {
        const includes: string[] = ['metadata.modified.byUser'];
        const fields: string[] = ['metadata.modified.byUser.description', 'metadata.modified.timestamp'];
        try {
            this.state.beginLoad();
            const response = await this.service.loadSession(sessionId, includes, fields);
            this.session = response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveSession(): Promise<boolean> {
        if(!this.context.currentSession) { return false; }
        try {
            this.state.beginSave();
            const response = await this.service.saveSession(this.context.currentSession);
            if(response.isSuccessful) {
                if(this.mounted) {
                    this.context.currentSession.id = response.id;
                }
            }
            else {
                throw new Error(response.resultText || 'Unable to save assessment');
            }

            return response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async completeAttachment(): Promise<boolean> {
        if(!this.attachmentId) { return false; }

        try {
            this.state.beginSave();
            const response = await this.service.completeAttachment(this.attachmentId);
            this.toaster.standard.completed('am:assessment.$', response.isSuccessful);

            if(response.isSuccessful) {
                this.events.publish(new UserTrackerContinueEvent());
                fireEvents(this.$element, 'assessment-complete');
            }
            return response.isSuccessful;
        }
        catch(error) {
            this.toaster.standard.completed('am:assessment.$', false);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    protected async reopenAssessment(): Promise<boolean> {
        if(!this.attachmentId) { return false; }

        try {
            this.state.beginSave();
            const response = await this.service.reopenAttachment(this.attachmentId);
            this.toaster.standard.activate('am:assessment.$', response.isSuccessful);

            if(response.isSuccessful && this.mounted) {
                this.readonly = false;
                this.menuConfigured = false;
                this.loadData(this.attachmentId);
            }

            return response.isSuccessful;
        }
        catch(error) {
            this.toaster.standard.activate('am:assessment.$', false);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

}
